import React, {
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  ArrowLeft,
  Camera,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

const API_BASE_URL =
  "https://www.frendrops.in/api";

interface LocationState {
  phone_number?: string;
  phone_prefix?: string;
}

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();

  const state =
    (location.state as LocationState) || {};

  const phoneNumber =
    state.phone_number ||
    sessionStorage.getItem(
      "login_mobile"
    ) ||
    "";

  const phonePrefix =
    state.phone_prefix ||
    sessionStorage.getItem(
      "login_phone_prefix"
    ) ||
    "+91";

  const fileInputRef =
    useRef<HTMLInputElement>(null);

  const [fullName, setFullName] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [address, setAddress] =
    useState("");

  const [profileImage, setProfileImage] =
    useState<File | null>(null);

  const [preview, setPreview] =
    useState<string | null>(null);

  const [errors, setErrors] =
    useState<Record<string, string>>({});

  const [loading, setLoading] =
    useState(false);

  const selectImage = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file =
      e.target.files?.[0];

    if (!file) return;

    setProfileImage(file);

    setPreview(
      URL.createObjectURL(file)
    );
  };

  const submitSignup = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    setLoading(true);
    setErrors({});

    try {
      const formData = new FormData();

      formData.append(
        "full_name",
        fullName.trim()
      );

      formData.append(
        "email",
        email.trim()
      );

      formData.append(
        "address",
        address.trim()
      );

      formData.append(
        "phone_number",
        phoneNumber
      );

      formData.append(
        "phone_prefix",
        phonePrefix
      );

      if (profileImage) {
        formData.append(
          "profile_image",
          profileImage
        );
      }

      const token =
        localStorage.getItem("token");

      const response =
        await axios.post(
          `${API_BASE_URL}/signup`,
          formData,
          {
            headers: {
              ...(token
                ? {
                    Authorization:
                      `Bearer ${token}`,
                  }
                : {}),
            },
          }
        );

      console.log(
        "Signup Response:",
        response.data
      );

      const backend =
        response.data;

      const loginToken =
        backend?.token ||
        backend?.data?.token;

      const user =
        backend?.data?.user ||
        backend?.data;

      if (!loginToken) {
        throw new Error(
          backend?.message ||
            "Signup failed. Please try again."
        );
      }

      localStorage.setItem(
        "token",
        loginToken
      );

      localStorage.setItem(
        "user",
        JSON.stringify({
          ...(user || {}),
          phone_prefix:
            phonePrefix,
          phone_number:
            phoneNumber,
          profile_complete: true,
        })
      );

      sessionStorage.removeItem(
        "login_mobile"
      );

      sessionStorage.removeItem(
        "login_phone_prefix"
      );

      navigate("/productList", {
        replace: true,
      });
    } catch (error: any) {
      console.error(
        "Signup Error:",
        error?.response?.data ||
          error
      );

      const response =
        error?.response?.data;

      /*
       * Same validation-error
       * structure handled by RN.
       */
      if (
        response?.message ===
          "Validation Error" &&
        Array.isArray(response?.data)
      ) {
        const fieldErrors: Record<
          string,
          string
        > = {};

        response.data.forEach(
          (item: any) => {
            if (
              item?.path &&
              item?.msg
            ) {
              fieldErrors[
                item.path
              ] = item.msg;
            }
          }
        );

        setErrors(fieldErrors);
        return;
      }

      setErrors({ form: response?.message || error?.message || "Something went wrong." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f6fb]">

      {/* Header */}
      <div className="px-4 pt-4">

        <button
          type="button"
          onClick={() => navigate(-1)}
          disabled={loading}
          className="p-2 rounded-full hover:bg-gray-200"
        >
          <ArrowLeft size={24} />
        </button>

      </div>

      <div className="max-w-md mx-auto px-5 pb-10">

        <h1 className="text-xl font-bold text-gray-800 mt-3 mb-6">
          Complete your profile
        </h1>

        {/* Profile image */}
        <div className="flex justify-center mb-6">

          <div className="relative">

            <div className="w-24 h-24 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">

              {preview ? (
                <img
                  src={preview}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Camera
                  size={36}
                  className="text-gray-500"
                />
              )}

            </div>

            <button
              type="button"
              onClick={() =>
                fileInputRef.current?.click()
              }
              disabled={loading}
              className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-purple-700 text-white flex items-center justify-center shadow"
            >
              <Camera size={18} />
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={selectImage}
              className="hidden"
            />

          </div>

        </div>

        <form
          onSubmit={submitSignup}
          className="space-y-4"
        >
          {errors.form && <p className="text-sm text-red-500">{errors.form}</p>}

          {/* Full name */}
          <div>

            <input
              type="text"
              placeholder="Full Name"
              value={fullName}
              disabled={loading}
              onChange={(e) => {
                setFullName(
                  e.target.value
                );

                setErrors((prev) => ({
                  ...prev,
                  full_name: "",
                }));
              }}
              className={`w-full border rounded-lg px-3 py-3 outline-none bg-white ${
                errors.full_name
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />

            {errors.full_name && (
              <p className="text-xs text-red-500 mt-1">
                {errors.full_name}
              </p>
            )}

          </div>

          {/* Email */}
          <div>

            <input
              type="email"
              placeholder="Email"
              value={email}
              disabled={loading}
              onChange={(e) => {
                setEmail(
                  e.target.value
                );

                setErrors((prev) => ({
                  ...prev,
                  email: "",
                }));
              }}
              className={`w-full border rounded-lg px-3 py-3 outline-none bg-white ${
                errors.email
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />

            {errors.email && (
              <p className="text-xs text-red-500 mt-1">
                {errors.email}
              </p>
            )}

          </div>

          {/* Address */}
          <div>

            <textarea
              placeholder="Address (city, state)"
              value={address}
              disabled={loading}
              onChange={(e) => {
                setAddress(
                  e.target.value
                );

                setErrors((prev) => ({
                  ...prev,
                  address: "",
                }));
              }}
              rows={4}
              className={`w-full border rounded-lg px-3 py-3 outline-none bg-white resize-none ${
                errors.address
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />

            {errors.address && (
              <p className="text-xs text-red-500 mt-1">
                {errors.address}
              </p>
            )}

          </div>

          {/* Mobile */}
          <input
            type="text"
            value={`${phonePrefix} ${phoneNumber}`}
            disabled
            className="w-full border rounded-lg px-3 py-3 bg-gray-100 text-gray-500"
          />

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3.5 rounded-xl text-white font-extrabold ${
              loading
                ? "bg-purple-300"
                : "bg-purple-700 hover:bg-purple-800"
            }`}
          >
            {loading
              ? "Saving..."
              : "Continue"}
          </button>

        </form>

      </div>
    </div>
  );
}